import { resolveWaitSeconds } from '~/utils/time'

/** 后端统一响应壳（若接口直接返回 data 本体，则不走此类型） */
export interface ApiEnvelope<T = unknown> {
  code: number
  message?: string
  msg?: string
  data: T
}

/** 业务成功码 */
export const API_SUCCESS_CODES = new Set([0, 200])

/** HTTP / 业务未授权码 */
export const API_UNAUTHORIZED_CODE = 401

/**
 * 资金密码连续输错被锁定（HTTP 403）：响应头 Retry-After，
 * data = { retry_after_seconds, locked_until }（平台 RetryInfo）
 */
export const FUND_PASSWORD_LOCKED_CODE = 1030

/** 锁定 / 频控响应体 data（平台 RetryInfo）；locked_until 只有锁定类错误才有 */
export interface RetryInfoDto {
  /** 还要等多少秒（向上取整，与 Retry-After 头同值） */
  retry_after_seconds: number
  /** 锁定截止时刻（时刻串） */
  locked_until?: string
}

/**
 * 错误响应里的剩余等待秒数：优先 data.retry_after_seconds，其次 Retry-After 头，
 * 其次 data.locked_until − serverNow()；都没有返回 null。
 */
export function retryWaitSeconds(data: unknown, headers?: Headers | null): number | null {
  const info = (data && typeof data === 'object' ? data : {}) as Partial<
    Record<keyof RetryInfoDto, unknown>
  >
  return resolveWaitSeconds({
    seconds: info.retry_after_seconds,
    retryAfter: headers?.get('Retry-After'),
    until: info.locked_until,
  })
}

export function isApiUnauthorized(status?: number, code?: number): boolean {
  return status === API_UNAUTHORIZED_CODE || code === API_UNAUTHORIZED_CODE
}

/** 业务 / HTTP 错误（拦截器抛出，调用方可按 code / status 分支） */
export class ApiError extends Error {
  readonly code: number
  readonly data: unknown
  readonly status?: number
  /** 响应头（频控 / 锁定时读 Retry-After）；请求没拿到响应时缺席 */
  readonly headers?: Headers

  constructor(
    message: string,
    options: { code: number; data?: unknown; status?: number; headers?: Headers },
  ) {
    super(message)
    this.name = 'ApiError'
    this.code = options.code
    this.data = options.data ?? null
    this.status = options.status
    this.headers = options.headers
  }

  get isUnauthorized(): boolean {
    return isApiUnauthorized(this.status, this.code)
  }

  /**
   * 服务端给出的剩余等待秒数（data.retry_after_seconds > Retry-After > locked_until − 此刻）；
   * 没给时为 null。倒计时以它为准，不要硬编码（见《前端接口规范》频控与等待）。
   */
  get retryAfterSeconds(): number | null {
    return retryWaitSeconds(this.data, this.headers)
  }
}
